import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

const TOAST_CSS_URL = 'https://uicdn.toast.com/editor/latest/toastui-editor.min.css';
const TOAST_JS_URL = 'https://uicdn.toast.com/editor/latest/toastui-editor-all.min.js';

function ensureToastAssets() {
  if (!document.querySelector(`link[href="${TOAST_CSS_URL}"]`)) {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = TOAST_CSS_URL;
    document.head.appendChild(link);
  }

  if (window.toastui?.Editor) return Promise.resolve(window.toastui.Editor);

  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${TOAST_JS_URL}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve(window.toastui?.Editor));
      existing.addEventListener('error', reject);
      return;
    }

    const script = document.createElement('script');
    script.src = TOAST_JS_URL;
    script.async = true;
    script.onload = () => resolve(window.toastui?.Editor);
    script.onerror = reject;
    document.body.appendChild(script);
  });
}

const ToastHtmlEditor = forwardRef(function ToastHtmlEditor(
  {
    initialHtml,
    placeholder = '본문을 입력하세요...',
    onChange,
  },
  ref
) {
  const rootRef = useRef(null);
  const editorRef = useRef(null);
  const [ready, setReady] = useState(false);

  useImperativeHandle(ref, () => ({
    getHTML: () => editorRef.current?.getHTML() || '',
    setHTML: (html) => {
      if (!editorRef.current) return;
      editorRef.current.setHTML(html || '', false);
    },
    insertImage: (url) => {
      if (!editorRef.current || !url) return;
      editorRef.current.exec('addImage', { imageUrl: url, altText: 'image' });
    },
  }), []);

  useEffect(() => {
    let mounted = true;

    ensureToastAssets()
      .then((EditorClass) => {
        if (!mounted || !rootRef.current || !EditorClass) return;

        editorRef.current = new EditorClass({
          el: rootRef.current,
          initialEditType: 'wysiwyg',
          previewStyle: 'vertical',
          height: '420px',
          initialValue: initialHtml || '',
          placeholder,
          usageStatistics: false,
          events: {
            change: () => {
              onChange?.(editorRef.current?.getHTML() || '');
            },
          },
        });

        setReady(true);
      })
      .catch(() => {
        setReady(false);
      });

    return () => {
      mounted = false;
      editorRef.current?.destroy();
      editorRef.current = null;
    };
  }, []); // mount once

  useEffect(() => {
    if (!editorRef.current || !ready) return;
    const current = editorRef.current.getHTML();
    if ((initialHtml || '') !== current) {
      editorRef.current.setHTML(initialHtml || '', false);
    }
  }, [initialHtml, ready]);

  return (
    <div className="toast-editor-wrapper">
      {!ready && <div className="loading">에디터 로딩 중...</div>}
      <div ref={rootRef} />
    </div>
  );
});

export default ToastHtmlEditor;
